// src/contexts/AuthContext.jsx - FIXED VERSION with Proper Auth Management
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { useSocket } from './SocketContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const socket = useSocket();
  
  // ========================================
  // 🎓 LEARNING: Refs for Managing State
  // ========================================
  // Refs persist across re-renders and don't trigger re-renders when changed
  // Perfect for flags and tracking async operations
  
  const isInitialized = useRef(false);        // Has auth been initialized?
  const interceptorId = useRef(null);         // Track axios interceptor
  const isRefreshing = useRef(false);         // Prevent concurrent refresh attempts
  const refreshPromise = useRef(null);        // Share refresh promise across requests
  const failedQueue = useRef([]);             // Queue failed requests during refresh
  const isMounted = useRef(true);             // Prevent state updates after unmount

  // ========================================
  // 🎓 LEARNING: Proper Queue Management
  // ========================================
  // When refresh fails, we need to reject ALL queued requests
  // When refresh succeeds, we need to retry ALL queued requests
  
  const processQueue = useCallback((error, token = null) => {
    console.log(`🔄 Processing queue: ${failedQueue.current.length} requests, error: ${!!error}`);
    
    failedQueue.current.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    
    failedQueue.current = [];
  }, []);

  
  const resetAuthState = useCallback(() => {
    console.log('🧹 Resetting auth state completely');
    
    // **CRITICAL FIX**: Disconnect WebSocket FIRST, before clearing tokens
    if (socket) {
      console.log('👋 Disconnecting WebSocket due to auth reset');
      socket.disconnect(); // This properly cleans up the WebSocket
    }
    
    // Broadcast reset event to all providers
    window.dispatchEvent(new CustomEvent('auth-state-reset'));
    
    // Clear tokens from storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    // Clear axios headers
    delete axios.defaults.headers.common['Authorization'];
    
    // Reset all React state (but only if component is still mounted)
    if (isMounted.current) {
      setCurrentUser(null);
      setIsAuthenticated(false);
      setError('');
      setLoading(false);
    }
    
    // Reset refs
    isInitialized.current = false;
    isRefreshing.current = false;
    refreshPromise.current = null;
    
    // Process any queued requests with error
    processQueue(new Error('Authentication failed'));
    
  }, [socket, processQueue]); // Remove socket from the dependency since we handle it first

  // ========================================
  // 🎓 LEARNING: Smart Token Refresh
  // ========================================
  // Only ONE refresh attempt at a time, others wait for the result
  
  const refreshAccessToken = useCallback(async () => {
    console.log('🔄 refreshAccessToken called');
    
    // If already refreshing, return the existing promise
    if (isRefreshing.current && refreshPromise.current) {
      console.log('⏳ Refresh already in progress, returning existing promise');
      return refreshPromise.current;
    }
    
    // Mark as refreshing and create promise
    isRefreshing.current = true;
    refreshPromise.current = (async () => {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        console.log('🔄 Attempting token refresh...');
        const response = await axios.post(`${API_ENDPOINTS.businessAuth}/refresh`, {
          refresh_token: refreshToken
        });

        if (response.data.status === 'success') {
          const { access_token } = response.data.data;
          
          // Store new token
          localStorage.setItem('access_token', access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          
          console.log('✅ Token refreshed successfully');
          
          // Reconnect WebSocket with new token
          if (socket && socket.isAuthenticated) {
            console.log('🔄 Reconnecting WebSocket with new token...');
            await socket.reconnect(access_token);
          }
          
          // Process queued requests with new token
          processQueue(null, access_token);
          
          return access_token;
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        
        // Complete auth reset on refresh failure
        resetAuthState();
        
        // Process queue with error
        processQueue(error);
        
        throw error;
      } finally {
        // Reset refresh state
        isRefreshing.current = false;
        refreshPromise.current = null;
      }
    })();
    
    return refreshPromise.current;
  }, [socket, processQueue, resetAuthState]);

  // ========================================
  // 🎓 LEARNING: Robust Axios Interceptor
  // ========================================
  // Handles token refresh transparently for all API calls
  
  useEffect(() => {
    console.log('🔧 Setting up axios interceptor');
    
    interceptorId.current = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Only handle 401 errors
        if (error.response?.status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        console.log('🔍 401 error detected:', originalRequest.url);

        // Don't refresh for auth endpoints (prevents infinite loops)
        if (originalRequest.url?.includes('/login') || 
            originalRequest.url?.includes('/refresh') ||
            originalRequest.url?.includes('/verify-partner-code') ||
            originalRequest.url?.includes('/complete-partner-setup')) {
          console.log('🚫 Auth endpoint failed, not attempting refresh');
          resetAuthState();
          return Promise.reject(error);
        }

        // Mark as retry attempt
        originalRequest._retry = true;

        try {
          // If already refreshing, queue this request
          if (isRefreshing.current) {
            console.log('⏳ Queueing request during refresh');
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ 
                resolve: (token) => {
                  originalRequest.headers['Authorization'] = `Bearer ${token}`;
                  resolve(axios(originalRequest));
                },
                reject
              });
            });
          }

          // Attempt refresh
          const newToken = await refreshAccessToken();
          
          // Retry original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(originalRequest);
          
        } catch (refreshError) {
          console.error('❌ Request failed after refresh attempt');
          return Promise.reject(refreshError);
        }
      }
    );

    // Cleanup interceptor
    return () => {
      if (interceptorId.current !== null) {
        console.log('🧹 Cleaning up axios interceptor');
        axios.interceptors.response.eject(interceptorId.current);
        interceptorId.current = null;
      }
    };
  }, [refreshAccessToken, resetAuthState]);

  // ========================================
  // 🎓 LEARNING: Safe Auth Check
  // ========================================
  // Only runs once, with proper error handling
  
  const checkAuth = useCallback(async () => {
    if (isInitialized.current) {
      console.log('🔄 Auth already initialized, skipping');
      return;
    }

    try {
      console.log('🔍 Initializing auth check...');
      isInitialized.current = true;
      
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        console.log('❌ No token found');
        if (isMounted.current) {
          setLoading(false);
        }
        return;
      }

      // Set token in headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Verify token
      console.log('🔍 Verifying token...');
      const response = await axios.get(`${API_ENDPOINTS.businessAuth}/me`);
      
      if (response.data.status === 'success' && isMounted.current) {
        console.log('✅ Token verified, user authenticated');
        
        // FIXED: Combine user and business data if available
        const userData = response.data.data;
        const fullUserData = userData.business ? {
          ...userData.user || userData,
          business: userData.business
        } : userData;
        
        setCurrentUser(fullUserData);
        setIsAuthenticated(true);
        
        // Connect WebSocket
        if (socket) {
          console.log('🔌 Connecting WebSocket...');
          await socket.connect(token);
        }
      }
    } catch (error) {
      console.error('❌ Auth check failed:', error);
      // Don't call resetAuthState here - let interceptor handle it
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [socket]);

  // ========================================
  // 🎓 LEARNING: Component Lifecycle
  // ========================================
  // Track mounting state to prevent memory leaks
  
  useEffect(() => {
    isMounted.current = true;
    checkAuth();
    
    return () => {
      isMounted.current = false;
    };
  }, [checkAuth]);

  // ========================================
  // PUBLIC AUTH METHODS
  // ========================================
  
  const login = async (email, password) => {
    try {
      setError('');
      setLoading(true);

      console.log('🔍 Attempting login...');
      const response = await axios.post(`${API_ENDPOINTS.businessAuth}/login`, {
        email,
        password
      });

      if (response.data.status === 'success') {
        // FIXED: Extract both user and business data
        const { access_token, refresh_token, user, business } = response.data.data;
        
        console.log('✅ Login successful');
        
        // Store tokens
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        // Set authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // FIXED: Combine user and business data
        const fullUserData = {
          ...user,
          business: business  // This adds the business object with the slug
        };
        
        // Update state (only if mounted)
        if (isMounted.current) {
          setCurrentUser(fullUserData);
          setIsAuthenticated(true);
        }
        
        // Connect WebSocket
        if (socket) {
          console.log('🔌 Connecting WebSocket after login...');
          await socket.connect(access_token);
        }
        
        return true;
      } else {
        if (isMounted.current) {
          setError(response.data.message || 'Login failed');
        }
        return false;
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      if (isMounted.current) {
        setError(error.response?.data?.message || 'Login failed. Please try again.');
      }
      return false;
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const logout = async () => {
    console.log('🔍 Logging out...');
    resetAuthState();
  };

  // Partner signup methods (simplified - same pattern)
  const verifyPartnerCode = async (code) => {
    try {
      setError('');
      setLoading(true);

      const response = await axios.post(`${API_ENDPOINTS.businessAuth}/verify-partner-code`, {
        partner_code: code
      });

      if (response.data.status === 'success') {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Code verification failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Code verification failed';
      if (isMounted.current) {
        setError(errorMessage);
      }
      throw new Error(errorMessage);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const completePartnerSetup = async (partnerCode, name, email, password) => {
    try {
      setError('');
      setLoading(true);

      const response = await axios.post(`${API_ENDPOINTS.businessAuth}/complete-partner-setup`, {
        partner_code: partnerCode,
        name,
        email,
        password
      });

      if (response.data.status === 'success') {
        // FIXED: Extract both user and business data
        const { access_token, refresh_token, user, business } = response.data.data;
        
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // FIXED: Combine user and business data
        const fullUserData = {
          ...user,
          business: business  // This adds the business object with the slug
        };
        
        if (isMounted.current) {
          setCurrentUser(fullUserData);
          setIsAuthenticated(true);
        }
        
        if (socket) {
          await socket.connect(access_token);
        }
        
        return true;
      } else {
        if (isMounted.current) {
          setError(response.data.message || 'Partner setup failed');
        }
        return false;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Partner setup failed';
      if (isMounted.current) {
        setError(errorMessage);
      }
      return false;
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const clearError = () => {
    if (isMounted.current) {
      setError('');
    }
  };

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    verifyPartnerCode,
    completePartnerSetup,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};